import React from "react";

import { Button, IconWrapper, Tooltip } from "@king-ict/eupisi-ui/components";
import { ReactComponent as InfoIcon } from "@king-ict/eupisi-ui/components/Icon/Info.svg";
import type { CodeBook } from "@king-ict/eupisi-ui/models";
import type { TFunction } from "i18next";
import { Col, Row } from "react-bootstrap";


import type { Institution } from "../../../models/Institution";
import type { ProgramRequest, ProgramRequestDetail } from "../../../models/ProgramRequest";
import { ProgramRequestStatus } from "../../../models/ProgramRequest/ProgramRequestStatus";
import { ProgramRequestType } from "../../../models/ProgramRequest/ProgramRequestType";
import { ResolutionDocumentType } from "../../../models/ProgramRequest/ResolutionDocumentType";
import { RoleEnum } from "../../../models/User/RoleEnum";
import type { UserSession } from "../../../models/User/UserSession";
import styles from "../ProgramRequestDetails.module.scss";

interface IProps {
  t: TFunction;
  userSession: UserSession | undefined;
  institution: Institution | undefined;
  programRequest: ProgramRequest;
  programRequestDetail: ProgramRequestDetail;
  programRequestCorrection: ProgramRequestDetail | undefined;
  isMinister: boolean;
  allowedStatusTransitions: CodeBook[];

  onSubmitPausedRequest: () => void;
  onChangeStatusClick: () => void;
  onEditRequest: () => void;
  onOfficeEditRequest: () => void;
  onAssignDifferentAdministratorClick: () => void;
  onCreateRequestResolution: (type: ResolutionDocumentType, isCorrection?: boolean) => void;
  handleConsentCreate: (type: ResolutionDocumentType) => void;
}

export function ProgramRequestDetailsActionBar({
  t,
  userSession,
  institution,
  programRequest,
  programRequestDetail,
  programRequestCorrection,
  isMinister,
  allowedStatusTransitions,
  onSubmitPausedRequest,
  onChangeStatusClick,
  onEditRequest,
  onOfficeEditRequest,
  onAssignDifferentAdministratorClick,
  onCreateRequestResolution,
  handleConsentCreate,
}: IProps) {
  return (
    <>
      {userSession &&
        userSession.roleId === RoleEnum.Helpdesk &&
        allowedStatusTransitions.length > 0 && (
          <Row className={styles.title}>
            <Col md={12}>
              <div className={styles.editButtonPanel}>
                <Button outline className={styles.actionButton} onClick={onChangeStatusClick}>
                  {t("manualStatusChange.changeStatusButton")}
                </Button>
              </div>
            </Col>
          </Row>
        )}

      {userSession &&
        (userSession.roleId === RoleEnum.InstitutionAdmin ||
          userSession.roleId === RoleEnum.InstitutionDirector) &&
        programRequest.requestStatusId === ProgramRequestStatus.Paused && (
          <Row>
            <Col md={12}>
              <div className={styles.editButtonPanel}>
                <Button onClick={onSubmitPausedRequest} className={styles.actionButton}>
                  {t("request.submitPausedRequest")}
                </Button>
              </div>
            </Col>
          </Row>
        )}

      {userSession &&
        (userSession.roleId === RoleEnum.InstitutionAdmin ||
          userSession.roleId === RoleEnum.InstitutionDirector) &&
        (programRequest.requestStatusId === ProgramRequestStatus.Opened ||
          programRequest.requestStatusId === ProgramRequestStatus.Evaluated ||
          programRequest.requestStatusId === ProgramRequestStatus.SentMZO ||
          programRequest.requestStatusId === ProgramRequestStatus.UnlockedASOO ||
          programRequest.requestStatusId === ProgramRequestStatus.UnlockedMZO ||
          programRequest.requestStatusId === ProgramRequestStatus.HarmonizationUnlockedASOO) &&
        programRequest.isEditable && (
          <Row>
            <Col md={12}>
              <div className={styles.editButtonPanel}>
                <Button
                  onClick={onEditRequest}
                  className={styles.actionButton}
                  disabled={
                    !institution ||
                    !institution.andragogicLeaders ||
                    institution.andragogicLeaders.length === 0
                  }
                >
                  {t("request.editRequest")}
                </Button>
              </div>
            </Col>
          </Row>
        )}

      {userSession &&
        (userSession.roleId === RoleEnum.MZOMOffice ||
          (userSession.roleId === RoleEnum.ASOOOffice && !isMinister)) &&
        (programRequest.programRequestDetail.envelope?.classificationMark ||
          programRequest.programRequestDetail.envelope?.registryNumber ||
          programRequest.programRequestDetail.envelope?.registrationDate) && (
          <Row>
            <Col md={12}>
              <div className={styles.editButtonPanel}>
                <Tooltip
                  overlay={
                    <div className={styles.infoTooltip}>
                      {userSession.roleId === RoleEnum.ASOOOffice
                        ? t("request.ASOOOficeTooltip")
                        : t("request.MZOOfficeTooltip")}
                    </div>
                  }
                  placement="bottom"
                  flip
                >
                  <IconWrapper color="primary">
                    <InfoIcon />
                  </IconWrapper>
                </Tooltip>
                <Button className={styles.actionButton} onClick={onOfficeEditRequest}>
                  {t("request.editRequest")}
                </Button>
              </div>
            </Col>
          </Row>
        )}

      {!isMinister &&
        userSession &&
        (userSession.roleId === RoleEnum.ASOOAdmin || userSession.roleId === RoleEnum.Helpdesk) &&
        programRequest.isEditable &&
        programRequest.programRequestDetail.envelope?.receivedById &&
        (programRequest.requestStatusId === ProgramRequestStatus.SubmittedASOO ||
          programRequest.requestStatusId === ProgramRequestStatus.HarmonizationASOO ||
          programRequest.requestStatusId === ProgramRequestStatus.CancelledASOO) && (
          <Row className={styles.title}>
            <Col md={12}>
              <div className={styles.editButtonPanel}>
                <Button
                  outline
                  className={styles.actionButton}
                  onClick={onAssignDifferentAdministratorClick}
                >
                  {t("request.assignDifferentAdministrator")}
                </Button>
              </div>
            </Col>
          </Row>
        )}

      {!isMinister &&
        userSession &&
        (userSession.roleId === RoleEnum.ASOO || userSession.roleId === RoleEnum.ASOOAdmin) &&
        programRequest.isEditable &&
        programRequest.programRequestDetail.envelope?.receivedById &&
        programRequest.programRequestDetail.envelope?.receivedById === userSession.userId &&
        ((programRequest.requestStatusId === ProgramRequestStatus.SubmittedASOO && (
          <Row className={styles.title}>
            <Col md={12}>
              <div className={styles.editButtonPanel}>
                <Button
                  outline
                  onClick={() => onCreateRequestResolution(ResolutionDocumentType.ExpertOpinion)}
                  className={styles.actionButton}
                >
                  {t("request.judgeRequest")}
                </Button>
                <Button
                  outline
                  onClick={() => onCreateRequestResolution(ResolutionDocumentType.UnlockRequestASOO)}
                  className={styles.actionButton}
                >
                  {t("request.unlockRequest")}
                </Button>
              </div>
            </Col>
          </Row>
        )) ||
          (programRequest.requestStatusId === ProgramRequestStatus.HarmonizationASOO && (
            <Row className={styles.title}>
              <Col md={12}>
                <div className={styles.editButtonPanel}>
                  {programRequestDetail.envelope?.programRequestTypeId ===
                    ProgramRequestType.ConsentForHarmonizationASOO && (
                    <>
                      <Button
                        outline
                        onClick={() => onCreateRequestResolution(ResolutionDocumentType.HarmonizationUnlockRequestASOO)}
                        className={styles.actionButton}
                      >
                        {t("request.unlockRequest")}
                      </Button>
                      <Button
                        outline
                        onClick={() => handleConsentCreate(ResolutionDocumentType.HarmonizationDecisionASOO)}
                        className={styles.actionButton}
                      >
                        {t("harmonization.ministryConsentButton")}
                      </Button>
                    </>
                  )}
                </div>
              </Col>
            </Row>
          )) ||
          (programRequest.requestStatusId === ProgramRequestStatus.CancelledASOO && (
            <Row className={styles.title}>
              <Col md={12}>
                <div className={styles.editButtonPanel}>
                  <Button
                    outline
                    onClick={() => handleConsentCreate(ResolutionDocumentType.ArchiveASOO)}
                    className={styles.actionButton}
                  >
                    {t("revocation.agencyRevocationButton")}
                  </Button>
                </div>
              </Col>
            </Row>
          )))}

      {userSession &&
        (userSession.roleId === RoleEnum.MZOMAdmin || userSession.roleId === RoleEnum.Helpdesk) &&
        programRequest.isEditable &&
        programRequest.programRequestDetail.envelope?.receivedById &&
        (programRequest.requestStatusId === ProgramRequestStatus.SubmittedMZO ||
          programRequest.requestStatusId === ProgramRequestStatus.SentMZO ||
          programRequest.requestStatusId === ProgramRequestStatus.ConsentMZO ||
          programRequest.requestStatusId === ProgramRequestStatus.CancelledMZO ||
          programRequest.requestStatusId === ProgramRequestStatus.PausedMZOM) && (
          <Row className={styles.title}>
            <Col md={12}>
              <div className={styles.editButtonPanel}>
                <Button
                  outline
                  className={styles.actionButton}
                  onClick={onAssignDifferentAdministratorClick}
                >
                  {t("request.assignDifferentAdministrator")}
                </Button>
              </div>
            </Col>
          </Row>
        )}

      {userSession &&
        (userSession.roleId === RoleEnum.MZOM ||
          userSession.roleId === RoleEnum.MZOMAdmin ||
          userSession.roleId === RoleEnum.Helpdesk) &&
        programRequest.isEditable &&
        programRequest.programRequestDetail.envelope?.receivedById &&
        programRequest.programRequestDetail.envelope?.receivedById === userSession.userId &&
        ((programRequest.requestStatusId === ProgramRequestStatus.SubmittedMZO && (
          <Row className={styles.title}>
            <Col md={12}>
              <div className={styles.editButtonPanel}>
                <Button
                  outline
                  onClick={() => onCreateRequestResolution(ResolutionDocumentType.ExecutionResolution)}
                  className={styles.actionButton}
                  disabled={
                    programRequest.programRequestResolutions?.find(
                      (v) => v.documentTypeId === ResolutionDocumentType.CommitteeMembership,
                    ) !== undefined &&
                    programRequest.programRequestResolutions?.find(
                      (v) => v.documentTypeId === ResolutionDocumentType.InspectionReport,
                    ) === undefined
                  }
                >
                  {t("request.executionRequest")}
                </Button>
                {programRequest.programRequestResolutions?.find(
                  (v) => v.documentTypeId === ResolutionDocumentType.CommitteeMembership,
                ) !== undefined &&
                  programRequest.programRequestResolutions?.find(
                    (v) => v.documentTypeId === ResolutionDocumentType.InspectionReport,
                  ) === undefined && (
                    <Button
                      outline
                      onClick={() => onCreateRequestResolution(ResolutionDocumentType.InspectionReport)}
                      className={styles.actionButton}
                      disabled={programRequestCorrection !== null}
                    >
                      {t("request.inspectionReportRequest")}
                    </Button>
                  )}
                {programRequest.programRequestResolutions?.find(
                  (v) => v.documentTypeId === ResolutionDocumentType.CommitteeMembership,
                ) === undefined && (
                  <Button
                    outline
                    onClick={() => onCreateRequestResolution(ResolutionDocumentType.CommitteeMembership)}
                    className={styles.actionButton}
                    disabled={programRequestCorrection !== null}
                  >
                    {t("request.committeeMembershipRequest")}
                  </Button>
                )}
                <Button
                  outline
                  onClick={() => onCreateRequestResolution(ResolutionDocumentType.UnlockRequestMZO)}
                  className={styles.actionButton}
                >
                  {t("request.unlockRequest")}
                </Button>
              </div>
            </Col>
          </Row>
        )) ||
          (programRequest.requestStatusId === ProgramRequestStatus.SentMZO && (
            <Row className={styles.title}>
              <Col md={12}>
                <div className={styles.editButtonPanel}>
                  <Button
                    outline
                    onClick={() => onCreateRequestResolution(ResolutionDocumentType.ExecutionResolution)}
                    className={styles.actionButton}
                  >
                    {t("request.executionRequest")}
                  </Button>
                </div>
              </Col>
            </Row>
          )) ||
          (programRequest.requestStatusId === ProgramRequestStatus.ConsentMZO && (
            <Row className={styles.title}>
              <Col md={12}>
                <div className={styles.editButtonPanel}>
                  {programRequestDetail.envelope?.programRequestTypeId ===
                    ProgramRequestType.ConsentForExecutionAtTheLocationOfAnotherLegalEntityMZO && (
                    <Button
                      outline
                      onClick={() =>
                        handleConsentCreate(ResolutionDocumentType.OtherLegalEntityLocationDecision)
                      }
                      className={styles.actionButton}
                    >
                      {t("otherLegalEntityLocation.ministryConsentButton")}
                    </Button>
                  )}
                  {programRequestDetail.envelope?.programRequestTypeId ===
                    ProgramRequestType.ConsentForExecutionInAForeignLanguage && (
                    <Button
                      outline
                      onClick={() =>
                        handleConsentCreate(ResolutionDocumentType.ForeignLanguageDecision)
                      }
                      className={styles.actionButton}
                    >
                      {t("teacherLanguage.ministryConsentButton")}
                    </Button>
                  )}
                  {programRequestDetail.envelope?.programRequestTypeId ===
                    ProgramRequestType.RequestForExecutionAtAnotherLocation && (
                    <Button
                      outline
                      onClick={() => handleConsentCreate(ResolutionDocumentType.OtherLocationDecision)}
                      className={styles.actionButton}
                    >
                      {t("otherLocations.ministryConsentButton")}
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
          )) ||
          (programRequest.requestStatusId === ProgramRequestStatus.CancelledMZO && (
            <Row className={styles.title}>
              <Col md={12}>
                <div className={styles.editButtonPanel}>
                  <Button
                    outline
                    onClick={() => handleConsentCreate(ResolutionDocumentType.ArchiveMZO)}
                    className={styles.actionButton}
                  >
                    {t("revocation.ministryRevocationButton")}
                  </Button>
                </div>
              </Col>
            </Row>
          )) ||
          (programRequest.requestStatusId === ProgramRequestStatus.PausedMZOM && (
            <Row className={styles.title}>
              <Col md={12}>
                <div className={styles.editButtonPanel}>
                  <Button
                    outline
                    onClick={() =>
                      onCreateRequestResolution(
                        ResolutionDocumentType.InstitutionBasicDataChangeResolution,
                      )
                    }
                    className={styles.actionButton}
                  >
                    {t("request.executionRequest")}
                  </Button>
                </div>
              </Col>
            </Row>
          )))}
    </>
  );
}

